import React, {FC, useCallback, useEffect, useState} from 'react'
import {Status} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {WidgetProps} from '@wix/yoshi-flow-editor'
import {Card, Divider, Text, TextButton, TextButtonPriority, Spinner} from 'wix-ui-tpa/cssVars'
import {utcToZonedTime} from '@wix/table-reservations-lib/timezone'
import {useSettings, useStyles} from '@wix/tpa-settings/react'
import {PaymentStatus as OrderPaymentStatus} from '@wix/ambassador-ep-orders/types'

import {Map} from '../components/Map'
import {getLongDate} from '../../../utils/dateString'
import {CancelModal} from '../components/CancelModal'
import {CancellationFeeNotice} from '../components/CancellationFeeNotice'
import {CancellationPolicyDialog} from '../components/CancellationPolicyDialog'
import {ConfirmationErrorModal} from '../components/ConfirmationErrorModal'
import {withReservationConfirmationStorageProvider} from '../storage'
import stylesParams from '../stylesParams'
import {RtlProvider} from '../../../components-shared/RtlProvider'
import {settingsParams} from '../settingsParams'
import {TextAlignment} from '../../../types/createStylesParams'
import {LayoutSize} from '../../../components-shared/LayoutSizeProvider/types'
import {withLayoutSizeProvider} from '../../../components-shared/LayoutSizeProvider'

import {useHooks} from './useHooks'
import {classes, st} from './Widget.st.css'
import {
  LAYOUT_BREAKPOINTS_START,
  ALLOWED_STATUSES,
  reservationConfirmationDataHooks,
  STATUS_TO_CTA_BUTTON_DATA_HOOK_MAP,
  STATUS_TO_KEYS_MAP,
} from './constants'

const Widget: FC<WidgetProps> = () => {
  const {
    t,
    isHydrated,
    layoutSize,
    isCancelDialogOpened,
    reservation,
    experience,
    orderDetails,
    shouldShowOrderDetails,
    formattedAddress,
    locationName,
    areaName,
    calendarHref,
    messageType,
    timeZone,
    goToNewReservation,
    openCancelDialog,
    closeCancelDialog,
    phoneNumber,
    regionalSettings,
    isLoading,
    isPerGuestPayment,
    pricePerUnitWithCurrency,
    onAddToCalendarClick,
    shouldShowErrorModal,
    cancellationPolicySubtitleOverride,
    shouldShowPendingCancellationFee,
    shouldShowChargedCancellationFee,
    appliedCancellationFeeAmount,
    prepaidTotalAmount,
    orderPaidAmount,
    orderRefundAmount,
    isCancellationFeeEnabled,
    feeWarning,
  } = useHooks()
  const styles = useStyles()
  const settings = useSettings()
  const [isPolicyOpen, setIsPolicyOpen] = useState(false)
  const openPolicy = useCallback(() => setIsPolicyOpen(true), [])
  const closePolicy = useCallback(() => setIsPolicyOpen(false), [])

  const titleTag =
    styles.get(stylesParams.titleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.titleFont).htmlTag

  const textAlignment = settings.get(settingsParams.textAlignment)
  const textAlignmentClass = TextAlignmentToClass[textAlignment]

  const subtitleTag =
    styles.get(stylesParams.subtitleFont).htmlTag ??
    styles.getDefaultValue(stylesParams.subtitleFont).htmlTag

  const formattedDate = getLongDate(
    utcToZonedTime(reservation?.details?.startDate ?? new Date(), timeZone),
    regionalSettings,
  )

  const isXS = layoutSize === LayoutSize.XS
  const reservationNotPaid =
    reservation && orderDetails?.paymentStatus === OrderPaymentStatus.NOT_PAID
  const orderRefunded = Boolean(orderRefundAmount)
  const isCanceledFeeSummary = shouldShowPendingCancellationFee || shouldShowChargedCancellationFee
  const canShowPolicy =
    isCanceledFeeSummary && feeWarning.feeApplies && Boolean(feeWarning.guestPolicy)

  useEffect(() => {
    if (!canShowPolicy) {
      setIsPolicyOpen(false)
    }
  }, [canShowPolicy])

  const prepaidLineAmount =
    prepaidTotalAmount ?? orderDetails?.priceSummary?.subtotal?.formattedAmount
  const prepaidOrderTotal = prepaidTotalAmount ?? orderDetails?.priceSummary?.total?.formattedAmount
  const paidAmount =
    orderPaidAmount ??
    (reservationNotPaid
      ? orderDetails?.balanceSummary?.paid?.formattedAmount
      : orderDetails?.priceSummary?.total?.formattedAmount)

  const renderCalendarCTA = (status?: Status) => {
    if (status === Status.RESERVED) {
      return (
        <TextButton
          as="a"
          href={calendarHref}
          // @ts-expect-error issue in types
          target="_blank"
          className={st(classes.linkButton, classes.calendarLink)}
          priority={TextButtonPriority.primary}
          onClick={onAddToCalendarClick}
        >
          {t('uou-reservations.reservation-confirmation.calendar-cta')}
        </TextButton>
      )
    }

    return null
  }

  const renderCancelCTA = () => {
    if (!reservation) {
      return null
    }

    return (
      <TextButton
        className={classes.linkButton}
        priority={TextButtonPriority.primary}
        data-hook={STATUS_TO_CTA_BUTTON_DATA_HOOK_MAP[reservation.status!]}
        disabled={!isHydrated}
        onClick={() => {
          if (reservation.status === Status.RESERVED || reservation.status === Status.REQUESTED) {
            openCancelDialog()
          } else {
            goToNewReservation(reservation)
          }
        }}
      >
        {t(STATUS_TO_KEYS_MAP[reservation.status!].cta)}
      </TextButton>
    )
  }

  const renderOrderDetails = () => {
    if (!reservation) {
      return null
    }

    if (isCanceledFeeSummary && !feeWarning.hasPrepaidOrder) {
      return (
        <div className={classes.orderDetails}>
          <Text className={classes.bodyText}>
            {t('uou-reservations.reservation-confirmation.order-summary')}
          </Text>
          {feeWarning.feeApplies ? (
            <CancellationFeeNotice
              t={t}
              feeWarning={feeWarning}
              className={classes.cancellationFeeNotice}
              onViewPolicy={openPolicy}
            />
          ) : null}
          <div className={classes.reservationInfoWrap}>
            <Text className={classes.bodyText}>
              {t('uou-reservations.reservation-confirmation.order-summary.cancellation-fee')}
            </Text>
            <Text className={classes.bodyText}>{appliedCancellationFeeAmount}</Text>
          </div>
        </div>
      )
    }

    return (
      <div className={classes.orderDetails}>
        <Text className={classes.bodyText}>
          {t('uou-reservations.reservation-confirmation.order-summary')}
        </Text>
        <div className={classes.reservationInfoWrap}>
          <Text className={classes.text} tagName="p">
            {isPerGuestPayment
              ? t('uou-reservations.shared.price-for-reserved-seats', {
                  pricePerGuest: pricePerUnitWithCurrency,
                  guestNumber: reservation.details?.partySize,
                })
              : t('uou-reservations.shared.price-for-party')}
          </Text>
          <Text className={classes.bodyText}>{prepaidLineAmount}</Text>
        </div>
        <div className={classes.reservationInfoWrap}>
          <Text className={classes.bodyText}>
            {t('uou-reservations.reservation-confirmation.order-summary.tax')}
          </Text>
          <Text className={classes.bodyText}>
            {orderDetails?.priceSummary?.tax?.formattedAmount}
          </Text>
        </div>
        <div className={classes.reservationInfoWrap}>
          <Text className={classes.bodyText}>
            {t('uou-reservations.reservation-confirmation.order-summary.total')}
          </Text>
          <Text className={classes.bodyText}>{prepaidOrderTotal}</Text>
        </div>
        <Divider className={classes.orderDivider} />
        {isCanceledFeeSummary && feeWarning.feeApplies ? (
          <CancellationFeeNotice
            t={t}
            feeWarning={feeWarning}
            className={classes.cancellationFeeNotice}
            onViewPolicy={openPolicy}
          />
        ) : null}
        <div className={classes.reservationInfoWrap}>
          <Text className={classes.bodyText}>
            {t('uou-reservations.reservation-confirmation.order-summary.paid')}
          </Text>
          <Text className={classes.bodyText}>{paidAmount}</Text>
        </div>
        {isCanceledFeeSummary ? (
          <div className={classes.reservationInfoWrap}>
            <Text className={classes.bodyText}>
              {t('uou-reservations.reservation-confirmation.order-summary.cancellation-fee')}
            </Text>
            <Text className={classes.bodyText}>{appliedCancellationFeeAmount}</Text>
          </div>
        ) : null}
        {orderRefunded && (
          <div className={classes.reservationInfoWrap}>
            <Text className={classes.bodyText}>
              {t('uou-reservations.reservation-confirmation.order-summary.refunded')}
            </Text>
            <Text className={classes.bodyText}>{orderRefundAmount}</Text>
          </div>
        )}
        {reservationNotPaid && !isCanceledFeeSummary && (
          <div className={classes.reservationInfoWrap}>
            <Text className={classes.bodyText}>
              {t('uou-reservations.reservation-confirmation.order-summary.due-later')}
            </Text>
            <Text className={classes.bodyText}>
              {orderDetails?.priceSummary?.total?.formattedAmount}
            </Text>
          </div>
        )}
      </div>
    )
  }

  if (isLoading) {
    return (
      <RtlProvider>
        <main className={st(classes.root, {[layoutSize]: true})}>
          <Spinner isCentered className={classes.loadingSpinner} />
        </main>
      </RtlProvider>
    )
  }

  if (!reservation?.id || shouldShowErrorModal) {
    return (
      <RtlProvider>
        <ConfirmationErrorModal />
      </RtlProvider>
    )
  }

  if (!(ALLOWED_STATUSES as unknown as Status[]).includes(reservation.status!)) {
    return (
      <RtlProvider>
        <main className={st(classes.root, {[layoutSize]: true}, textAlignmentClass)}>
          <Text tagName={titleTag} className={classes.title}>
            {t('uou-reservations.reservation-confirmation.no-available')}
          </Text>
        </main>
      </RtlProvider>
    )
  }

  let titleText

  switch (reservation.status) {
    case Status.RESERVED: {
      titleText = settings.get(settingsParams.titleReservedText)
      break
    }

    case Status.REQUESTED: {
      titleText = settings.get(settingsParams.titleRequestedText)
      break
    }

    default: {
      titleText = t(STATUS_TO_KEYS_MAP[reservation.status!].title)
    }
  }

  return (
    <RtlProvider>
      <main
        className={st(classes.root, {[layoutSize]: true})}
        data-hook={reservationConfirmationDataHooks.root()}
      >
        <div className={classes.contentWrapper}>
          <Text
            tagName={titleTag}
            className={st(classes.title, textAlignmentClass)}
            role={reservation.status === Status.CANCELED ? 'alert' : undefined}
          >
            {titleText}
          </Text>
          <Text tagName={subtitleTag} className={st(classes.subtitle, textAlignmentClass)}>
            {t(
              cancellationPolicySubtitleOverride ??
                STATUS_TO_KEYS_MAP[reservation.status!].subtitle,
              {messageType},
            )}
          </Text>

          {isXS && <Divider className={classes.mobileDivider} />}

          <Card className={classes.reservationDetails}>
            <Card.Container className={classes.reservationInfo}>
              <div className={classes.reservationInfoWrap}>
                <div className={classes.reservationInfoText}>
                  <Text
                    className={st(classes.reservationInfoTextTitle, classes.bodyText)}
                    tagName="h2"
                  >
                    {t(STATUS_TO_KEYS_MAP[reservation.status!].details, {
                      formattedDate,
                      partySize: t('uou-reservations.shared.guests', {
                        number: reservation.details?.partySize ?? 0,
                      }),
                    })}
                  </Text>

                  {experience ? (
                    <Text className={st(classes.reservationInfoTextTitle, classes.bodyText)}>
                      {experience.configuration?.displayInfo?.name}
                    </Text>
                  ) : null}

                  {reservation.status !== Status.DECLINED ? (
                    <>
                      <Text className={classes.bodyText}>
                        {formattedDate}
                        {!isXS ? ', ' : ''}
                      </Text>
                      {isXS ? <br /> : null}
                      <Text className={classes.bodyText}>
                        {t('uou-reservations.shared.guests', {
                          number: reservation.details?.partySize ?? 0,
                        })}
                        {!isXS && areaName ? ', ' : ''}
                      </Text>
                      {isXS && areaName ? <br /> : null}
                      {areaName ? <Text className={classes.bodyText}>{areaName}</Text> : null}
                    </>
                  ) : null}
                </div>

                {!isXS && renderCalendarCTA(reservation.status)}
              </div>

              {reservation.status === Status.DECLINED && reservation.declineReason ? (
                <div className={classes.declineReasonWrapper}>
                  <Text className={classes.bodyText}>"{reservation.declineReason}"</Text>
                </div>
              ) : null}

              <Divider className={classes.divider} />

              {shouldShowOrderDetails && (
                <>
                  {reservationNotPaid && !isCanceledFeeSummary ? (
                    <div className={classes.notPaidOrderDetails}>
                      <div className={classes.notPaidOrderDetailsMessage}>
                        <Text className={classes.bodyText}>
                          {reservation.status === Status.REQUESTED
                            ? t(
                                'uou-reservations.reservation-confirmation.order-summary.charge-once-approve',
                              )
                            : t(
                                'uou-reservations.reservation-confirmation.order-summary.reservation-approved-but-not-charged',
                              )}
                        </Text>
                        {!isXS && renderCancelCTA()}
                      </div>
                      {renderOrderDetails()}
                    </div>
                  ) : (
                    renderOrderDetails()
                  )}
                </>
              )}

              {isXS && renderCalendarCTA(reservation.status)}

              {(!reservationNotPaid || isCanceledFeeSummary) && renderCancelCTA()}
              {reservationNotPaid && !isCanceledFeeSummary && isXS && renderCancelCTA()}
            </Card.Container>
          </Card>

          {isXS && <Divider className={classes.mobileDivider} />}

          <Card className={classes.locationDetails}>
            <Card.Container className={classes.locationInfo}>
              <Text className={classes.bodyText} tagName="h2">
                {t('uou-reservations.reservation-confirmation.address-text')}
              </Text>
              <Text className={classes.bodyText} tagName="p">
                {locationName}
              </Text>
              <Text className={classes.bodyText} tagName="p">
                {formattedAddress}
              </Text>
              {phoneNumber ? (
                <Text
                  /* @ts-expect-error */
                  href={`tel:${phoneNumber}`}
                  className={classes.bodyText}
                  tagName="a"
                >
                  {t('uou-reservations.reservation-confirmation.phone-number', {phoneNumber})}
                </Text>
              ) : null}
            </Card.Container>

            <Card.Container className={classes.locationMap}>
              <Map address={formattedAddress} />
            </Card.Container>
          </Card>

          <CancelModal isOpen={isCancelDialogOpened} onClose={closeCancelDialog} />
          <CancellationPolicyDialog
            t={t}
            isOpen={isPolicyOpen && canShowPolicy}
            policy={feeWarning.guestPolicy}
            onClose={closePolicy}
          />
        </div>
      </main>
    </RtlProvider>
  )
}

const TextAlignmentToClass: Record<TextAlignment, string> = {
  [TextAlignment.Left]: classes.left,
  [TextAlignment.Center]: classes.center,
  [TextAlignment.Right]: classes.right,
}

export default withLayoutSizeProvider(
  withReservationConfirmationStorageProvider(Widget),
  LAYOUT_BREAKPOINTS_START,
)
